import { useEffect, useRef } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
import varSound from 'src/Sounds/var_sound.mp3';

/**
 * Plays a cue the moment a match's VAR review STARTS — `lastVarActive`
 * transitioning false→true on the backend-authored `MatchState`. Fires once
 * per review (re-arms only after `lastVarActive` clears); the backend already
 * dedupes repeated `Started` re-emits of the same review, so no extra
 * incident-key tracking is needed here. The first observed value only seeds
 * the baseline (no cue on tab open / a review already in progress).
 *
 * Baseline resets when `fixtureId` changes so switching matches never fires a
 * spurious cue off the new match's current VAR state.
 */
export function useVarReviewSound(
  active: boolean | undefined,
  opts: { enabled: boolean; fixtureId: number | null },
): void {
  const { load } = useAudioPlayer();
  const prevActive = useRef<boolean | undefined>(undefined);
  const prevFixture = useRef<number | null>(null);

  useEffect(() => {
    if (opts.fixtureId !== prevFixture.current) {
      prevFixture.current = opts.fixtureId;
      prevActive.current = undefined;
    }

    if (opts.enabled && prevActive.current === false && active === true) {
      try {
        load(varSound, { autoplay: true, loop: false });
      } catch {
        /* audio unavailable — the banner still renders */
      }
    }
    prevActive.current = active;
  }, [active, opts.enabled, opts.fixtureId, load]);
}
